<template>
  <b-modal
    id="debitos-encerramento-mit"
    :ref="'debitos-encerramento-mit'"
    title="Encerramento - Resumo dos Débitos Apurados"
    no-close-on-backdrop
    no-stacking
    scrollable
    size="lg"
    @hidden="fecharModal"
  >
    <div
      v-if="carregando"
      class="d-flex align-items-center justify-content-center p-4 text-muted"
    >
      <b-spinner
        small
        class="mr-2"
      />
      Carregando débitos de encerramento...
    </div>
    <div v-else>
      <!-- Cabeçalho com empresa e período -->
      <div class="mb-3 font-medium-1 p-2 bg-light border rounded">
        <strong>{{ empresaNome || 'Empresa' }}</strong>
        <span v-if="periodoApuracao"> | Período da Apuração: {{ periodoApuracao }}</span>
      </div>
      <b-row>
        <b-col
          v-for="grupo in gruposDebitos"
          :key="grupo.key"
          cols="6"
          class="mb-1 font-medium-2"
        >
          <b-form-group :label="grupo.label">
            <b-form-input
              :id="`debito-${grupo.key}`"
              v-b-tooltip.hover
              :value="formatarValor(obterValorDebito(grupo.key))"
              readonly
              class="font-medium-1"
              :title="grupo.tooltip"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Resumo final -->
      <div class="d-flex justify-content-end mt-1">
        <div class="p-3 font-medium-2 text-white bg-primary mr-2 rounded">
          <strong>Total Apurado:</strong> {{ formatarValor(totalApurado) }}
        </div>
        <div class="p-3 font-medium-2 text-white bg-secondary rounded">
          <strong>Suspensões:</strong> {{ formatarValor(totalSuspensoes) }}
        </div>
      </div>
    </div>

    <template v-slot:modal-footer="{ ok }">
      <b-button
        variant="primary"
        @click="ok()"
      >
        Fechar
      </b-button>
    </template>
  </b-modal>
</template>

<style lang="scss">
@import '../../styles/debitosEncerramento.scss';
</style>

<script>
import service from '@app/mit-apuracao/services/mitApuracaoService'
import gruposDebitos from '@app/mit-apuracao/config/gruposDebitos'

export default {
  name: 'DebitosEncerramentoMitApuracao',
  props: {
    empresaNome: { type: String, default: '' },
    empresaId: { type: Number, required: true },
    idApuracao: { type: Number, required: true },
    periodoApuracao: { type: String, default: '' },
  },
  data() {
    return {
      dados: null,
      payloadEntry: null,
      carregando: false,
      gruposDebitos,
      pagina: 1,
      tamanhoPagina: 50,
      suspensoes: [],
      carregandoSuspensoes: false,
    }
  },
  computed: {
    totalApurado() {
      // Calcula o total somando todos os valores de débitos
      return this.gruposDebitos.reduce((total, grupo) => {
        const valor = this.obterValorDebito(grupo.key)
        return total + valor
      }, 0)
    },
    totalSuspensoes() {
      // Calcula o total das suspensões
      if (!Array.isArray(this.suspensoes)) return 0

      return this.suspensoes.reduce((total, suspensao) => {
        if (!Array.isArray(suspensao.debitosSuspensos)) return total

        const totalSuspensao = suspensao.debitosSuspensos.reduce((subtotal, debito) => {
          const valor = Number(debito.valorSuspenso || 0)
          return subtotal + (Number.isFinite(valor) ? valor : 0)
        }, 0)

        return total + totalSuspensao
      }, 0)
    },
  },
  mounted() {
    this.$bvModal.show('debitos-encerramento-mit')
    this.carregarDebitosEncerramento()
    this.carregarSuspensoes()
  },
  methods: {
    fecharModal() {
      this.$emit('hidden')
    },

    carregarDebitosEncerramento() {
      this.carregando = true

      const filtroBody = {
        empresas: [this.empresaId],
        filtroBy: [
          { colunas: ['IdApuracao'], valor: this.idApuracao },
        ],
        ordemBy: [],
      }

      service.getDebitosEncerramento(this.pagina, this.tamanhoPagina, filtroBody)
        .then(({ data }) => {
          // Backend retorna EntidadePaginadoResponse com envelope { Pagina, Total, TamanhoPagina, Valores: [MitDebitosEncerramentoView] }
          this.dados = data || null

          // Seleciona a entrada correta de Valores
          const getValores = resp => {
            if (Array.isArray(resp?.Valores)) return resp.Valores
            if (Array.isArray(resp?.valores)) return resp.valores
            if (Array.isArray(resp?.dados)) return resp.dados
            if (resp?.dados && typeof resp.dados === 'object') return [resp.dados]
            return []
          }

          const valores = getValores(data)

          // Tenta filtrar por empresa e apuração quando disponíveis
          const byEmpresa = valores.filter(v => {
            const emp = Number(v.EmpresaId ?? v.empresaId)
            return !Number.isNaN(emp) && emp === Number(this.empresaId)
          })

          const pool = byEmpresa.length ? byEmpresa : valores
          this.payloadEntry = pool.find(v => {
            const ap = Number(v.IdApuracao ?? v.idApuracao)
            return !Number.isNaN(ap) && ap === Number(this.idApuracao)
          }) || pool[0] || null
        })
        .catch(error => {
          this.$erro('Erro ao carregar débitos de encerramento:', error)
          this.dados = null
          this.payloadEntry = null
        })
        .finally(() => {
          this.carregando = false
        })
    },

    obterValorDebito(chaveGrupo) {
      if (!this.payloadEntry || !chaveGrupo) return 0

      // Mapeia as chaves do config para as chaves reais da resposta do backend
      const mapeamentoChaves = {
        Irpj: 'irpj',
        Csll: 'csll',
        Irrf: 'irrf',
        Ipi: 'ipi',
        Iof: 'iof',
        PisPasep: 'pisPasep',
        Cofins: 'cofins',
        ContribuicoesDiversas: 'contribuicoesDiversas',
        Cpss: 'cpss',
        RetPagamentoUnificado: 'retPagamentoUnificado',
      }

      // Obtém a chave correta para acessar a resposta
      const chaveBackend = mapeamentoChaves[chaveGrupo] || chaveGrupo.charAt(0).toLowerCase() + chaveGrupo.slice(1)

      // Acessa o objeto do grupo de débito
      const grupoDebito = this.payloadEntry[chaveBackend]

      if (!grupoDebito || typeof grupoDebito !== 'object') return 0

      // Retorna o totalValor do grupo
      const totalValor = grupoDebito.totalValor ?? grupoDebito.TotalValor ?? 0

      // Garante que é um número válido
      const valor = Number(totalValor)
      return Number.isFinite(valor) ? valor : 0
    },

    formatarValor(valor) {
      // Formata o valor como moeda brasileira
      if (!valor || valor === 0) return 'R$ 0,00'

      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(valor)
    },

    carregarSuspensoes() {
      this.carregandoSuspensoes = true

      const filtroBody = {
        empresaId: this.empresaId,
        filtros: {
          idApuracao: this.idApuracao,
        },
        ordenacao: {
          campo: 'PeriodoApuracao',
          direcao: 'DESC',
        },
      }

      service.getSuspensoes(this.pagina, this.tamanhoPagina, filtroBody)
        .then(({ data }) => {
          // Processa a resposta do backend
          const getDados = resp => {
            if (Array.isArray(resp?.dados)) return resp.dados
            if (Array.isArray(resp?.Dados)) return resp.Dados
            if (Array.isArray(resp?.valores)) return resp.valores
            if (Array.isArray(resp?.Valores)) return resp.Valores
            if (resp?.dados && typeof resp.dados === 'object') return [resp.dados]
            return []
          }

          const dados = getDados(data)

          // Filtra por empresa e apuração quando disponíveis
          const byEmpresa = dados.filter(d => {
            const emp = Number(d.empresaId ?? d.EmpresaId)
            return !Number.isNaN(emp) && emp === Number(this.empresaId)
          })

          const pool = byEmpresa.length ? byEmpresa : dados
          const entry = pool.find(d => {
            const ap = Number(d.idApuracao ?? d.IdApuracao)
            return !Number.isNaN(ap) && ap === Number(this.idApuracao)
          }) || pool[0] || null

          // Extrai as suspensões do entry encontrado
          if (entry && entry.suspensoes) {
            this.suspensoes = Array.isArray(entry.suspensoes) ? entry.suspensoes : []
          } else {
            this.suspensoes = []
          }
        })
        .catch(error => {
          this.$erro('Erro ao carregar suspensões:', error)
          this.suspensoes = []
        })
        .finally(() => {
          this.carregandoSuspensoes = false
        })
    },
  },
}
</script>
